import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import API from '../../../services/API';
import { useLocation, useNavigate } from 'react-router-dom';

const StudentProfilePage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const studentId = queryParams.get('student_id');

    const currentMonth = new Date().toISOString().slice(0, 7);

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [loading, setLoading] = useState(false);
    const [profile, setProfile] = useState(null);
    const [lateDetails, setLateDetails] = useState([]);
    const [lateLoading, setLateLoading] = useState(false);
    const [attendanceYearSummary, setAttendanceYearSummary] = useState(null);
    const fetchStudentProfile = async (student_id, month) => {
        if (!student_id) {
            toast.error("Missing student_id");
            return;
        }

        setLoading(true);
        try {
            const headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'token': localStorage.getItem('token'),
            };

            let url = `/student/studentProfile?student_id=${student_id}&is_portal=true`;

            if (month && month !== "YEAR") {
                url += `&month=${month}`;
            } else {
                url += `&year=1`;
            }

            const res = await API.get(url, { headers });

            if (res.data.response_code === 1000) {
                setProfile(res.data.data || {});
            } else {
                toast.error(res.data.message || "Failed to load student profile");
                setProfile(null);
            }
        } catch (err) {
            toast.error(err?.message || "Error fetching student profile");
            setProfile(null);
        } finally {
            setLoading(false);
        }
    };
    const fetchStudentSummary = (studentId) => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        API.get(`/analytics/attendance/studentSummary?student_id=${studentId}`, { headers })
            .then((response) => {
                console.log("attendance year summary:", response.data.data);

                if (response.data.response_code === 1000) {
                    setAttendanceYearSummary(response.data.data || null);
                } else {
                    toast.error(response.data.message || "Failed to load summary");
                }
            })
            .catch(() => toast.error("Error fetching summary"));
    };
const fetchStudentLateDetails = async (studentId) => {
    if (!studentId) return;

    setLateLoading(true);
    try {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        const params = {
            is_portal: true,
            ...(selectedMonth !== "YEAR" ? { month: selectedMonth } : { year: 1 })
        };

        const res = await API.get(`/analytics/attendance/latecomers/${studentId}`, { 
            headers, 
            params 
        });

        if (res?.data?.response_code === 1000) {
            setLateDetails(res.data.data || []);
        } else {
            setLateDetails([]);
            toast.error(res.data.message || 'Unable to sync records');
        }
    } catch (err) {
        setLateDetails([]);
        toast.error('Network error while fetching punctuality data');
    } finally {
        setLateLoading(false);
    }
};
    useEffect(() => {
        if (studentId) {
            fetchStudentProfile(studentId, selectedMonth);
            fetchStudentLateDetails(studentId);
        }
    }, [studentId, selectedMonth]);

    useEffect(() => {
        if (studentId) {
            fetchStudentSummary(studentId);
        }
    }, [studentId]);
    const student = profile?.student || {};
    const attendanceSummary = profile?.attendance_summary || {};
    const punctualitySummary = profile?.punctuality_summary || {};
    const badDeeds = profile?.bad_deeds || [];
    const badDeedSummary = profile?.bad_deed_summary || {};
    const badDeedCategories = profile?.bad_deed_categories || [];
    const charts = profile?.charts || {};
    const calendar = profile?.calendar || [];
    const highlights = profile?.highlights || [];
    const goodDeeds = profile?.good_deeds || [];
    const goodDeedSummary = profile?.good_deed_summary || {};
    const goodDeedCategories = profile?.good_deed_categories || [];

    const isYearView = selectedMonth === "YEAR" || String(profile?.month || "").length === 4;

    const formatDate = (value) => {
        if (!value) return "-";
        return new Date(value).toLocaleDateString("ms-MY");
    };

    const formatTime = (value) => {
        if (!value) return "-";
        return new Date(value).toLocaleTimeString("ms-MY", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const StatCard = ({ title, value, tone = "dark", subtitle }) => {
        const toneMap = {
            success: "text-success",
            danger: "text-danger",
            warning: "text-warning",
            primary: "text-primary",
            dark: "text-dark",
            muted: "text-muted",
        };

        return (
            <div className="col-6 col-md-3 col-xl">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                    <div className="card-body">
                        <div className="text-muted small mb-2">{title}</div>
                        <div className={`fw-bold fs-2 ${toneMap[tone] || "text-dark"}`}>{value}</div>
                        {subtitle ? <div className="small text-muted mt-1">{subtitle}</div> : null}
                    </div>
                </div>
            </div>
        );
    };

    const SectionCard = ({ title, titleClass = "text-dark", children, bodyClassName = "" }) => (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-header bg-white rounded-top-4 py-3 border-bottom">
                <h5 className={`mb-0 fw-bold ${titleClass}`}>{title}</h5>
            </div>
            <div className={`card-body ${bodyClassName}`}>{children}</div>
        </div>
    );

    const EmptyState = ({ text }) => (
        <div className="text-center text-muted py-5">{text}</div>
    );

    const renderCategoryTable = (rows, emptyMessage) => (
        rows.length > 0 ? (
            <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Category</th>
                            <th className="text-center">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((item, idx) => (
                            <tr key={idx}>
                                <td>{item.category || "-"}</td>
                                <td className="text-center">{item.total || 0}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ) : (
            <EmptyState text={emptyMessage} />
        )
    );

    const renderDeedTable = (rows, emptyMessage) => (
        rows.length > 0 ? (
            <div className="table-responsive">
                <table className="table table-sm table-striped align-middle mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Date</th>
                            <th>Time</th>
                            <th>Reason</th>
                            <th>Remarks</th>
                            <th>Status</th>
                            <th>Teacher</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((item, idx) => (
                            <tr key={idx}>
                                <td>{formatDate(item.date)}</td>
                                <td>{formatTime(item.deed_time_myt)}</td>
                                <td>{item.deed_reason || "-"}</td>
                                <td>{item.remarks || "-"}</td>
                                <td>{item.status || "-"}</td>
                                <td>{item.teacher_name || "-"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        ) : (
            <EmptyState text={emptyMessage} />
        )
    );


    return (
        <Fragment>
            <div className="container-fluid mt-4">
                <div className="container-fluid pt-5 mt-3"> {/* Added padding to clear the top bar */}
                    <div className="row align-items-center mb-4 pb-4 g-3 border-bottom shadow-sm bg-white rounded-4 p-3">
                        {/* Left: Navigation & Branding */}
                        <div className="col-lg-7">
                            <div className="d-flex align-items-center">
                                {/* Modern Minimalist Back Button */}
                
                                <div>
                                    <h2 className="h4 mb-0 fw-black text-dark tracking-tight">
                                        Student <span className="text-danger">Profile Report</span>
                                    </h2>
                                    <div className="d-flex align-items-center gap-2 mt-1">
                                        <span className="badge bg-danger-subtle text-danger px-2 py-1 rounded-pill fw-bold" style={{ fontSize: '0.7rem' }}>
                                            OFFICIAL RECORD
                                        </span>
                                        <span className="text-muted small d-none d-sm-inline">
                                            Detailed insights into attendance & behavior
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Modern Filter Controls */}
                        <div className="col-lg-5">
                            <div className="d-flex gap-3 justify-content-lg-end align-items-center">
                                {/* Custom Date Input Group */}
                                <div className="input-group input-group-sm shadow-sm rounded-pill overflow-hidden border" style={{ maxWidth: '200px' }}>
                                    <span className="input-group-text bg-white border-0 ps-3">
                                        📅
                                    </span>
                                    <input
                                        type="month"
                                        className="form-control border-0 fw-bold"
                                        style={{ boxShadow: 'none', cursor: 'pointer' }}
                                        value={selectedMonth === "YEAR" ? currentMonth : selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                    />
                                </div>

                                {/* Year Toggle Button */}
                                <button
                                    className={`btn btn-sm rounded-pill px-4 fw-black transition-all shadow-sm ${selectedMonth === "YEAR"
                                        ? "btn-danger border-danger"
                                        : "btn-outline-dark bg-white"
                                        }`}
                                    onClick={() => setSelectedMonth("YEAR")}
                                    style={{ height: '38px', minWidth: '120px' }}
                                >
                                    {selectedMonth === "YEAR" ? '✓ Full Year' : 'Full Year'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body text-center text-muted py-5">
                            Loading student profile...
                        </div>
                    </div>
                ) : !studentId ? (
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body text-center text-danger py-5">
                            Missing student_id in URL
                        </div>
                    </div>
                ) : !profile ? (
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body text-center text-muted py-5">
                            No student profile found
                        </div>
                    </div>
                ) : (
                    <div className="row g-4">
                        <div className="col-12">
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                                <div className="card-body p-4">
                                    <div className="row align-items-center g-4">
                                        <div className="col-auto">
                                            {student.profile_pic ? (
                                                <img
                                                    src={student.profile_pic}
                                                    alt={student.student_name}
                                                    style={{
                                                        width: 108,
                                                        height: 108,
                                                        borderRadius: '24px',
                                                        objectFit: 'cover',
                                                        border: '1px solid #ececec',
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    className="d-flex align-items-center justify-content-center bg-light border"
                                                    style={{
                                                        width: 108,
                                                        height: 108,
                                                        borderRadius: '24px'
                                                    }}
                                                >
                                                    <span className="text-muted small">No Photo</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="col">
                                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                                <h4 className="mb-0 fw-bold text-dark">
                                                    {student.student_name || "Unknown Student"}
                                                </h4>
                                                <span className="badge bg-light text-dark border">
                                                    {student.class_name || "-"}
                                                </span>
                                            </div>


                                            <div className="d-flex flex-wrap gap-2">
                                                <span className="badge bg-success-subtle text-success border px-3 py-2">
                                                    Present {attendanceSummary.present_days ?? 0}
                                                </span>
                                                <span className="badge bg-danger-subtle text-danger border px-3 py-2">
                                                    Absent {attendanceSummary.absent_days ?? 0}
                                                </span>
                                                <span className="badge bg-warning-subtle text-warning border px-3 py-2">
                                                    Late {punctualitySummary.late_count ?? 0}
                                                </span>
                                                <span className="badge bg-primary-subtle text-primary border px-3 py-2">
                                                    Good Deeds {goodDeedSummary.total_good_deeds ?? goodDeeds.length ?? 0}
                                                </span>
                                                <span className="badge bg-dark-subtle text-dark border px-3 py-2">
                                                    Bad Deeds {badDeedSummary.total_bad_deeds ?? 0}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="col-lg-auto">
                                            <div className="text-lg-end">
                                                <div className="text-muted small">Attendance Rate</div>
                                                <div className="fw-bold fs-2 text-primary">
                                                    {attendanceSummary.attendance_rate ?? 0}%
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-12">
                            <div className="row g-3">
                                <StatCard
                                    title="School Days"
                                    value={attendanceSummary.total_school_days ?? 0}
                                    tone="dark"
                                />
                                <StatCard
                                    title="Present Days"
                                    value={attendanceSummary.present_days ?? 0}
                                    tone="success"
                                />
                                <StatCard
                                    title="Absent Days"
                                    value={attendanceSummary.absent_days ?? 0}
                                    tone="danger"
                                />
                                <StatCard
                                    title="Late Count"
                                    value={punctualitySummary.late_count ?? 0}
                                    tone="warning"
                                />
                                <StatCard
                                    title="Unrecorded"
                                    value={attendanceSummary.unrecorded_days ?? 0}
                                    tone="muted"
                                />
                            </div>
                        </div>
                        {attendanceYearSummary?.summary?.length > 0 && (
<SectionCard title="Yearly Attendance Explorer" titleClass="text-danger" bodyClassName="bg-light p-3">
    {attendanceYearSummary?.summary?.length > 0 ? (
        <div className="vstack gap-3" style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '8px' }}>
            {attendanceYearSummary.summary.map((month, idx) => (
                <div key={idx} className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="row g-0">
                        {/* 1. LEFT: THE MONTHLY STATS (Fixed Width) */}
                        <div className="col-md-4 bg-white border-end d-flex flex-column justify-content-center p-3 text-center">
                            <div className="tiny fw-black text-muted text-uppercase mb-1">{attendanceYearSummary.year}</div>
                            <h5 className="fw-900 text-dark mb-3 text-uppercase tracking-wide">{month.month}</h5>
                            
                            <div className="d-flex justify-content-center gap-2">
                                <div className="px-2">
                                    <div className="h5 fw-black text-success mb-0">{month.present_days}</div>
                                    <div className="tiny fw-bold text-muted">PRESENT</div>
                                </div>
                                <div className="vr opacity-25"></div>
                                <div className="px-2">
                                    <div className={`h5 fw-black ${month.absent_days > 0 ? 'text-danger' : 'text-muted'} mb-0`}>
                                        {month.absent_days}
                                    </div>
                                    <div className="tiny fw-bold text-muted">ABSENT</div>
                                </div>
                            </div>
                        </div>

                        {/* 2. RIGHT: THE LOG/DETAILS (Flexible) */}
                        <div className="col-md-8 bg-white p-3">
                            <div className="h-100 d-flex flex-column justify-content-center">
                                {month.details?.length > 0 ? (
                                    <div className="vstack gap-2">
                                        <div className="tiny fw-black text-danger border-bottom pb-1 mb-1">INCIDENT LOG</div>
                                        {month.details.map((detail, i) => (
                                            <div key={i} className="d-flex align-items-center justify-content-between gap-3">
                                                <div className="small">
                                                    <span className="fw-bold text-dark me-1">{detail.date}:</span>
                                                    <span className="text-muted">{detail.reason || "Unspecified"}</span>
                                                </div>
                                                {detail.letter_attachment_url && (
                                                    <a href={detail.letter_attachment_url} target="_blank" rel="noopener noreferrer" 
                                                       className="btn btn-sm btn-light border text-primary rounded-pill py-0 px-2 tiny fw-black">
                                                        VIEW MC
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-2">
                                        <span className="badge bg-success-subtle text-success rounded-pill px-3 py-1 fw-black tiny">
                                            ✓ 100% PERFECT ATTENDANCE
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    ) : (
        <EmptyState text="No yearly data synced yet." />
    )}
</SectionCard>
                        )}
                        <div className="col-12">
<SectionCard title="Late Arrival Details" titleClass="text-danger" bodyClassName="p-0">
    {lateLoading ? (
        <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-danger" /></div>
    ) : lateDetails.length > 0 ? (
        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table className="table table-hover align-middle mb-0">
                <thead className="sticky-top bg-white shadow-sm" style={{ zIndex: 1 }}>
                    <tr>
                        <th className="ps-4 py-3 border-0 text-muted tiny fw-black text-uppercase">Date</th>
                        <th className="py-3 border-0 text-muted tiny fw-black text-uppercase">Check In</th>
                        <th className="py-3 border-0 text-muted tiny fw-black text-uppercase">Session</th>
                        <th className="py-3 border-0 text-muted tiny fw-black text-uppercase pe-4">Remarks</th>
                    </tr>
                </thead>
                <tbody className="border-top-0">
                    {lateDetails.map((item, idx) => {
                        const dateStr = item.date ? new Date(item.date).toLocaleDateString("ms-MY", { 
                            day: '2-digit', 
                            month: 'short' 
                        }) : "-";

                        return (
                            <tr key={idx} className="border-bottom-light">
                                <td className="ps-4 py-3">
                                    <span className="fw-bold text-dark">{dateStr}</span>
                                </td>
                                <td>
                                    <span className="badge bg-danger-subtle text-danger rounded-pill px-3 py-2 fw-black" style={{ fontSize: '0.85rem' }}>
                                        {item.checkin_time || "00:00"}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-muted small fw-medium">{item.session || "Morning"}</span>
                                </td>
                                <td className="pe-4">
                                    <div className="text-dark small lh-sm" style={{ maxWidth: '300px' }}>
                                        {item.remarks || <span className="opacity-25 italic">No remarks</span>}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    ) : (
        <div className="py-5 text-center text-muted">No late records found.</div>
    )}
</SectionCard>
                        </div>
                        <div className="col-12 col-xl-6">
                            <SectionCard title="Good Deed Categories" titleClass="text-primary">
                                {renderCategoryTable(
                                    goodDeedCategories,
                                    goodDeedSummary.message || "No good deed recorded"
                                )}
                            </SectionCard>
                        </div>

                        <div className="col-12 col-xl-6">
                            <SectionCard title="Bad Deed Categories" titleClass="text-danger">
                                {renderCategoryTable(
                                    badDeedCategories,
                                    badDeedSummary.message || "No bad deed recorded"
                                )}
                            </SectionCard>
                        </div>

                        <div className="col-12">
                            <SectionCard title="Recent Good Deeds" titleClass="text-primary">
                                {renderDeedTable(
                                    goodDeeds,
                                    goodDeedSummary.message || "No good deed recorded"
                                )}
                            </SectionCard>
                        </div>

                        <div className="col-12">
                            <SectionCard title="Recent Bad Deeds" titleClass="text-danger">
                                {renderDeedTable(
                                    badDeeds,
                                    badDeedSummary.message || "No bad deed recorded"
                                )}
                            </SectionCard>
                        </div>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default StudentProfilePage;